// ── 曜日・祝日・ダイヤ種別ユーティリティ ──
// DepotPage / FavoritesPage / ArchivePage / ArchiveModal / SightingModal で共通利用する。

// ── 特別日について ──
//
// 特別日（暦どおりでないダイヤで走る日）の持ち主は **バックエンドの
// special_dates テーブル**で、GET /api/day-types/special で配られる。
// 管理画面 /admin/special-dates から足せるので、ここを編集する必要はない。
//
// 下の 2 つは API が取れなかったときのフォールバック。DayTypeProvider が
// 起動時に API を叩いて setSpecialDates() で差し替える。
// 取れなければこの値のまま動く（日付の色が少し古くなるだけで壊れはしない）。

// 日祝ダイヤで運行する特別日（'YYYY-MM-DD'）。
export const SPECIAL_HOLIDAY_DATES = [
  '2026-08-13',
  '2026-08-14',
  '2026-08-15',
];

// 土日祝だが平日ダイヤで運行する日（'YYYY-MM-DD'）。
// SPECIAL_HOLIDAY_DATES より優先される。
export const SPECIAL_WEEKDAY_DATES = [];

let _specialHoliday = new Set(SPECIAL_HOLIDAY_DATES);
let _specialWeekday = new Set(SPECIAL_WEEKDAY_DATES);

const _sameSet = (set, list) =>
  set.size === list.length && list.every((d) => set.has(d));

// API から取った特別日で差し替える。中身が変わったときだけ true を返す。
// getDayType は同期のまま保ちたい（レンダー中に呼ばれている）ので、
// モジュール内の Set を入れ替える形にしている。再描画は呼び出し側の責任。
export const setSpecialDates = ({ special_holidays, special_weekdays } = {}) => {
  const holidays = Array.isArray(special_holidays) ? special_holidays : [];
  const weekdays = Array.isArray(special_weekdays) ? special_weekdays : [];

  if (_sameSet(_specialHoliday, holidays) && _sameSet(_specialWeekday, weekdays)) {
    return false;
  }

  _specialHoliday = new Set(holidays);
  _specialWeekday = new Set(weekdays);
  return true;
};

const _getNthWeekday = (year, month, weekday, n) => {
  const d = new Date(year, month - 1, 1);
  let count = 0;
  while (d.getMonth() === month - 1) {
    if (d.getDay() === weekday) { count++; if (count === n) return `${year}-${String(month).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
    d.setDate(d.getDate() + 1);
  }
  return null;
};

const _buildHolidaySet = (year) => {
  const fmt = (m, d) => `${year}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  const set = new Set([fmt(1,1),fmt(2,11),fmt(2,23),fmt(4,29),fmt(5,3),fmt(5,4),fmt(5,5),fmt(8,11),fmt(11,3),fmt(11,23)]);
  set.add(fmt(3, Math.floor(20.8431 + 0.242194*(year-1980) - Math.floor((year-1980)/4)))); // 春分
  set.add(fmt(9, Math.floor(23.2488 + 0.242194*(year-1980) - Math.floor((year-1980)/4)))); // 秋分
  [_getNthWeekday(year,1,1,2), _getNthWeekday(year,7,1,3), _getNthWeekday(year,9,1,3), _getNthWeekday(year,10,1,2)]
    .filter(Boolean).forEach(d => set.add(d));
  // 振替休日
  [...set].forEach(ds => {
    const d = new Date(ds + 'T00:00:00');
    if (d.getDay() === 0) {
      const sub = new Date(d); sub.setDate(sub.getDate() + 1);
      const sf = dt => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
      while (set.has(sf(sub))) sub.setDate(sub.getDate() + 1);
      set.add(sf(sub));
    }
  });
  return set;
};

const _holidayCache = {};

// 'weekday' | 'sat' | 'holiday' を返す。holiday = 日祝ダイヤ運行日。
export const getDayType = (dateStr) => {
  if (!dateStr) return 'weekday';
  if (_specialWeekday.has(dateStr)) return 'weekday';
  if (_specialHoliday.has(dateStr)) return 'holiday';
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  if (dow === 6) return 'sat';
  const year = d.getFullYear();
  if (!_holidayCache[year]) _holidayCache[year] = _buildHolidaySet(year);
  if (dow === 0 || _holidayCache[year].has(dateStr)) return 'holiday';
  return 'weekday';
};

// マトリクス表のヘッダー／セル配色
export const COL = {
  weekday: { header: '#047857', hBorder: '#065f46', cellEven: '#f0fdf4', cellOdd: '#fff',    hover: '#bbf7d0' },
  sat:     { header: '#1e40af', hBorder: '#1e3a8a', cellEven: '#dbeafe', cellOdd: '#eff6ff', hover: '#bfdbfe' },
  holiday: { header: '#b91c1c', hBorder: '#991b1b', cellEven: '#ffe4e6', cellOdd: '#fff1f2', hover: '#fecaca' },
};

// モーダル内の日付バッジ配色
export const DATE_BADGE = {
  weekday: { bg: '#047857', border: '#065f46', color: '#fff' },
  sat:     { bg: '#1e40af', border: '#1e3a8a', color: '#fff' },
  holiday: { bg: '#b91c1c', border: '#991b1b', color: '#fff' },
};
